import * as React from "react";

import {
  getHomeBannerSlides,
  getDefaultHomeBannerSlides,
  setHomeBannerSlides,
  HOME_BANNER_UPDATED_EVENT,
  HOME_BANNER_STORAGE_KEY,
} from "../utils/homeBannerStore";

const EMPTY_FORM = {
  title: "",
  tag: "",
  link: "",
  tagLink: "",
  image: "",
  thumb: "",
  mobilePosition: "",
  headline: "",
  mobileHeadline: "",
  sub: "",
  cta: "",
};

function buildId() {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `bn_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function normalizeUrl(value) {
  return String(value || "").trim();
}

function normalizeImage(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
  if (raw.startsWith("/")) return raw;
  return raw;
}

export default function HomeBannerManager() {
  const [slides, setSlides] = React.useState(() => getHomeBannerSlides({ includeStoredOverrides: true }));
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [editingId, setEditingId] = React.useState("");
  const imageInputRef = React.useRef(null);
  const thumbInputRef = React.useRef(null);

  React.useEffect(() => {
    const refresh = () => setSlides(getHomeBannerSlides({ includeStoredOverrides: true }));
    window.addEventListener("storage", refresh);
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    window.addEventListener(HOME_BANNER_UPDATED_EVENT, refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
      window.removeEventListener(HOME_BANNER_UPDATED_EVENT, refresh);
    };
  }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId("");
  };

  const saveAll = (next) => {
    setHomeBannerSlides(next);
    setSlides(next);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const title = String(form.title || "").trim();
    const link = normalizeUrl(form.link);
    const image = normalizeImage(form.image);
    const thumb = normalizeImage(form.thumb);
    if (!title) return window.alert("Title is required.");
    if (!link) return window.alert("Link is required.");
    if (!image) return window.alert("Image is required.");

    const payload = {
      id: editingId || buildId(),
      title,
      tag: String(form.tag || "").trim(),
      link,
      tagLink: normalizeUrl(form.tagLink),
      image,
      thumb,
      mobilePosition: String(form.mobilePosition || "").trim(),
      headline: String(form.headline || "").trim(),
      mobileHeadline: String(form.mobileHeadline || "").trim(),
      sub: String(form.sub || "").trim(),
      cta: String(form.cta || "").trim(),
    };

    const next = [...slides];
    if (editingId) {
      const index = next.findIndex((s) => s.id === editingId);
      if (index !== -1) next[index] = payload;
      saveAll(next);
      resetForm();
      return;
    }
    next.unshift(payload);
    saveAll(next);
    resetForm();
  };

  const startEdit = (slide) => {
    setEditingId(slide.id);
    setForm({
      title: slide.title || "",
      tag: slide.tag || "",
      link: slide.link || "",
      tagLink: slide.tagLink || "",
      image: slide.image || "",
      thumb: slide.thumb || "",
      mobilePosition: slide.mobilePosition || "",
      headline: slide.headline || "",
      mobileHeadline: slide.mobileHeadline || "",
      sub: slide.sub || "",
      cta: slide.cta || "",
    });
  };

  const removeSlide = (id) => {
    if (!window.confirm("Delete this banner slide?")) return;
    saveAll(slides.filter((s) => s.id !== id));
    if (editingId === id) resetForm();
  };

  const resetToDefault = () => {
    if (!window.confirm("Reset to default Home Banner? This clears local overrides.")) return;
    window.localStorage.removeItem(HOME_BANNER_STORAGE_KEY);
    window.dispatchEvent(new Event(HOME_BANNER_UPDATED_EVENT));
    setSlides(getDefaultHomeBannerSlides());
    resetForm();
  };

  return (
    <div className="grid grid-cols-1 gap-7 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
      <section className="rounded-[20px] border border-[#d9d9d9] bg-white p-6 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-black">Home Page Banner Manager</h2>
            <p className="mt-1 text-sm text-[#4b5563]">Controls the Home page hero banner slides via local storage (no deploy required).</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={resetToDefault} className="rounded-full border border-[#dc2626] px-5 py-2 text-xs font-semibold tracking-[0.4px] uppercase text-[#dc2626] hover:bg-[#dc2626] hover:text-white">Reset Default</button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block text-sm font-semibold text-[#1f2937]">Title * (alt text)
            <input value={form.title} onChange={(e) => setForm((c) => ({ ...c, title: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
          </label>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="text-sm font-semibold text-[#1f2937]">Tag
              <input value={form.tag} onChange={(e) => setForm((c) => ({ ...c, tag: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
            </label>
            <label className="text-sm font-semibold text-[#1f2937]">Tag Link
              <input value={form.tagLink} onChange={(e) => setForm((c) => ({ ...c, tagLink: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
            </label>
          </div>
          <label className="block text-sm font-semibold text-[#1f2937]">Link *
            <input value={form.link} onChange={(e) => setForm((c) => ({ ...c, link: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
          </label>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="text-sm font-semibold text-[#1f2937]">Image * (URL or /assets path)
              <div className="mt-2 flex flex-col gap-2 md:flex-row md:items-center">
                <input value={form.image} onChange={(e) => setForm((c) => ({ ...c, image: e.target.value }))} className="w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => setForm((c) => ({ ...c, image: String(reader.result || "") }));
                    reader.readAsDataURL(file);
                  }}
                />
                <button type="button" onClick={() => imageInputRef.current?.click()} className="rounded-full border border-black px-4 py-2 text-[11px] font-semibold uppercase hover:bg-black hover:text-white">Upload</button>
              </div>
            </label>
            <label className="text-sm font-semibold text-[#1f2937]">Thumb (optional)
              <div className="mt-2 flex flex-col gap-2 md:flex-row md:items-center">
                <input value={form.thumb} onChange={(e) => setForm((c) => ({ ...c, thumb: e.target.value }))} className="w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                <input
                  ref={thumbInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => setForm((c) => ({ ...c, thumb: String(reader.result || "") }));
                    reader.readAsDataURL(file);
                  }}
                />
                <button type="button" onClick={() => thumbInputRef.current?.click()} className="rounded-full border border-black px-4 py-2 text-[11px] font-semibold uppercase hover:bg-black hover:text-white">Upload</button>
              </div>
            </label>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="text-sm font-semibold text-[#1f2937]">Headline
              <input value={form.headline} onChange={(e) => setForm((c) => ({ ...c, headline: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
            </label>
            <label className="text-sm font-semibold text-[#1f2937]">Mobile Headline
              <input value={form.mobileHeadline} onChange={(e) => setForm((c) => ({ ...c, mobileHeadline: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
            </label>
          </div>
          <label className="block text-sm font-semibold text-[#1f2937]">Sub
            <input value={form.sub} onChange={(e) => setForm((c) => ({ ...c, sub: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
          </label>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="text-sm font-semibold text-[#1f2937]">CTA
              <input value={form.cta} onChange={(e) => setForm((c) => ({ ...c, cta: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
            </label>
            <label className="text-sm font-semibold text-[#1f2937]">Mobile Position (e.g. 95% center)
              <input value={form.mobilePosition} onChange={(e) => setForm((c) => ({ ...c, mobilePosition: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
            </label>
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="submit" className="rounded-full bg-[#e01919] px-6 py-3 text-xs font-semibold tracking-[0.4px] uppercase text-white hover:bg-black">
              {editingId ? "Update Slide" : "Add Slide"}
            </button>
            {editingId && (
              <button type="button" onClick={resetForm} className="rounded-full border border-black px-6 py-3 text-xs font-semibold uppercase hover:bg-black hover:text-white">
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="rounded-[20px] border border-[#d9d9d9] bg-white p-6 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
        <h3 className="mb-4 text-sm font-bold text-black">Slides ({slides.length})</h3>
        <ul className="space-y-2">
          {slides.map((slide) => (
            <li key={slide.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#e5e7eb] bg-[#f9fafb] px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-black">{slide.tag ? `${slide.tag} · ` : ""}{slide.title}</p>
                <p className="truncate text-xs text-[#6b7280]">{slide.link}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button type="button" onClick={() => startEdit(slide)} className="rounded-full border border-black px-3 py-1 text-[10px] font-semibold uppercase hover:bg-black hover:text-white">Edit</button>
                <button type="button" onClick={() => removeSlide(slide.id)} className="rounded-full border border-[#dc2626] px-3 py-1 text-[10px] font-semibold uppercase text-[#dc2626] hover:bg-[#dc2626] hover:text-white">Delete</button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
